import { useContext, useEffect, useState } from "react"
import { Field, useFormikContext } from "formik"
import { DateTime } from "luxon"
import _ from "lodash"
import { FormContext } from "../../../context/formContext"
import { Calendar } from "primereact/calendar"
import fieldVisibility from "../../../helpers/fieldVisibility"
import {
  helperStates,
  useServiceDateValidation,
} from "../../../helpers/serviceDateValidationHelper"
import { UserContext } from "../../../context/userContext"
import { LoaderSmall } from "../../Loaders"

import styles from "../../../styles/Form.module.css"

export default function TypeSessionDate({ field, block, index }) {
  const form = useContext(FormContext)
  const formik = useFormikContext()
  const userCtx = useContext(UserContext)
  const [sessionDateError, setSessionDateError] = useState(null)
  const sessionFieldValue = formik.values[field.field_name]
  const sessionFieldError = formik.errors[field.field_name]

  const {
    status: serviceDateValidationStatus,
    initError: serviceDateValidationError,
    initializeServiceDateValidation,
    checkServiceDate,
  } = useServiceDateValidation()

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }
  const isRequired = checkIfRequired()

  const handleMinDate = () => {
    switch (field.field_name) {
      default:
        return null
    }
  }

  const handleMaxDate = () => {
    switch (field.field_name) {
      default:
        return new Date()
    }
  }

  useEffect(() => {
    const init = async () => {
      // Register field as loading in FormContext
      form && form.registerLoadingField(field)

      // Initialize service date validation
      await initializeServiceDateValidation({
        participant_membership_id: userCtx?.participant_membership_id,
        idTemplate: form?.idTemplate || null,
        field_name: field.field_name,
        isEdit: form?.editMode || false,
      })

      // Set initial value to current date if value is not already a valid date, for session date fields only
      const fieldSessionDate = formik?.getFieldProps(field.field_name)
      if (!_.isDate(fieldSessionDate.value)) {
        const initialValue = DateTime.now()
        const now = initialValue.toUTC().toJSDate()
        const end = initialValue.plus({ minutes: 15 }).toUTC().toJSDate()

        formik.setValues({
          ...formik.values,
          [field.field_name]: now,
          session_start_time: now,
          session_end_time: end,
          session_length: "15",
        })
      }

      // Notify FormContext that field is loaded
      form && form.completeLoadingField(field)
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!sessionDateError) return

    const current = sessionFieldValue
    if (!current || !_.isDate(current)) return

    // If Formik cleared our error, re-check and restore if still invalid
    if (!sessionFieldError) {
      const stillInvalid = checkServiceDate({ checkDate: current })
      if (stillInvalid) {
        formik.setFieldTouched(field.field_name, true, false)
        formik.setFieldError(field.field_name, stillInvalid)
      } else {
        setSessionDateError(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.field_name, sessionFieldValue, sessionFieldError, sessionDateError])

  const applySessionDateValidation = (dateValue) => {
    const error = checkServiceDate({ checkDate: dateValue }) || undefined
    setSessionDateError(error || null)

    formik.setFieldTouched(field.field_name, true, false)
    formik.setFieldError(field.field_name, error)

    return error
  }

  const handleChange = (e) => {
    const fieldStartTime = formik?.getFieldProps("session_start_time")
    const fieldEndTime = formik?.getFieldProps("session_end_time")
    const sessionDate = DateTime.fromJSDate(e.value).toObject()
    const startTime = DateTime.fromJSDate(fieldStartTime.value).toObject()
    const endTime = DateTime.fromJSDate(fieldEndTime.value).toObject()

    const x = DateTime.fromFormat(
      `${sessionDate.year}-${sessionDate.month}-${sessionDate.day} ${
        startTime?.hour || "00"
      }:${startTime?.minute || "00"}`,
      "y-M-d h:m",
    ).toJSDate()

    const y = DateTime.fromFormat(
      `${sessionDate.year}-${sessionDate.month}-${sessionDate.day} ${
        endTime?.hour || "00"
      }:${endTime?.minute || "00"}`,
      "y-M-d h:m",
    ).toJSDate()

    formik.setValues(
      {
        ...formik.values,
        [field.field_name]: x,
        session_start_time: x,
        session_end_time: y,
      },
      false,
    )

    applySessionDateValidation(x)
  }

  const getYearRange = () => {
    const currentYear = new Date().getFullYear()
    return `${currentYear - 1}:${currentYear}`
  }

  if (
    serviceDateValidationStatus !== helperStates.READY &&
    !serviceDateValidationError
  ) {
    return <LoaderSmall />
  }

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: fieldVisibility(field.field_visibility),
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field
          name={field.field_name}
          validate={() => sessionDateError || undefined}
        >
          {({ field: formikField, meta }) => (
            <>
              <Calendar
                name={formikField.name}
                value={formikField.value}
                onChange={(e) => handleChange(e)}
                disabled={isDisabled}
                onBlur={formikField.onBlur}
                maxDate={handleMaxDate()}
                minDate={handleMinDate()}
                monthNavigator
                yearNavigator
                yearRange={getYearRange()}
              />
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}
